/**
 * component-drawer.js — <drawer-element>
 *
 * A generic slide-over panel. Deliberately generic: the navigation drawer
 * (F3), search panel (F3), filter sheet (F5) and cart drawer (F8) are the
 * same component with different content, so focus management, scroll locking
 * and escape handling are implemented and audited exactly once.
 *
 * Markup contract
 * ---------------
 *   <drawer-element id="NavDrawer" data-position="start">
 *     <div class="drawer__scrim" data-drawer-close></div>
 *     <div class="drawer__panel" role="dialog" aria-modal="true"
 *          aria-labelledby="NavDrawerTitle" tabindex="-1">
 *       <h2 id="NavDrawerTitle">…</h2>
 *       <button type="button" data-drawer-close>…</button>
 *     </div>
 *   </drawer-element>
 *
 * Any element on the page can open it:
 *   <button type="button" data-drawer-open="NavDrawer" aria-expanded="false">
 *
 * Visibility is driven by CSS `visibility`, not the `hidden` attribute or
 * `display:none`. Two reasons: `display` cannot be transitioned, and
 * `visibility:hidden` already removes descendants from both the tab order and
 * the accessibility tree — so no `inert` polyfill and no `aria-hidden`
 * bookkeeping is required, and there is no window in which a closed drawer is
 * still reachable by Tab.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Single-execution guard. NOT optional.
  //
  // This file is loaded by header.liquid, cart-drawer.liquid AND
  // main-collection.liquid, because each of those sections must work on its
  // own. Classic scripts sharing a src are fetched once but EXECUTED once per
  // <script> tag — so without this guard the delegated click listener at the
  // bottom is registered two or three times, toggle() runs once per listener,
  // and the drawer opens then instantly closes again.
  //
  // The symptom is that the menu button appears to do nothing at all, with no
  // console error. customElements.define is separately guarded and would not
  // have caught this.
  // ---------------------------------------------------------------------
  if (window.__themeDrawerLoaded) return;
  window.__themeDrawerLoaded = true;

  /** Open drawers, innermost last. Lets Escape close only the topmost. */
  let stack = [];

  class Drawer extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel') || this;
      this.addEventListener('click', this.onClick);

      // A drawer still open across a bfcache restore would leave the page
      // scroll-locked and focus trapped with no visible cause.
      window.addEventListener('pagehide', this.onPageHide);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('pagehide', this.onPageHide);
      if (this.open) this.close();
    }

    get open() {
      return this.hasAttribute('open');
    }

    /**
     * @param {HTMLElement} [opener] Control that triggered the open. Its
     *   aria-expanded is kept in sync. Focus restoration is handled by
     *   trapFocus, which captured the opener as the previously-focused
     *   element at the moment show() ran.
     */
    show(opener) {
      if (this.open) return;

      // `opener` serves two purposes: the aria-expanded owner, and the element
      // focus returns to on close. Those are not always the same control.
      //
      // The header's menu/cart/search buttons are disclosure controls and
      // declare aria-expanded in markup. An add-to-cart submit button is not —
      // it is only a focus-return target — so the attribute is set only where
      // it already exists, rather than invented on a control whose state it
      // does not describe.
      this.opener = opener || null;
      if (this.opener && this.opener.hasAttribute('aria-expanded')) {
        this.opener.setAttribute('aria-expanded', 'true');
      }

      this.setAttribute('open', '');
      window.Theme.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      stack.push(this);

      // Focus the panel itself rather than its first control: a screen reader
      // then announces the dialog's accessible name and role before its
      // contents, which is what tells the user where they have landed.
      //
      // A drawer whose entire purpose is one input (search) opts out via
      // [data-drawer-autofocus] — there, landing on the field is what the user
      // asked for, and the dialog name is still announced as its context.
      const preferred = this.panel.querySelector('[data-drawer-autofocus]');
      this.releaseFocus = window.Theme.trapFocus(this.panel, preferred || this.panel);

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.open) return;

      this.removeAttribute('open');
      window.Theme.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      stack = stack.filter((drawer) => drawer !== this);

      const opener = this.opener;
      if (opener) {
        if (opener.hasAttribute('aria-expanded')) {
          opener.setAttribute('aria-expanded', 'false');
        }
        this.opener = null;
      }

      // release() removes the trap listener and restores focus to whatever was
      // focused when the trap was installed.
      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }

      // Then explicitly return focus to the opener, overriding that restore.
      //
      // Relying on "whatever was focused before" is not good enough: a
      // programmatic .click(), a click on an SVG child, or a drawer opened
      // from code all leave focus somewhere other than the trigger. The
      // drawer knows which control opened it, and that is where a keyboard
      // user must land.
      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle(opener) {
      if (this.open) {
        this.close();
      } else {
        this.show(opener);
      }
    }

    onClick(event) {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      // Only the topmost drawer responds, so a filter sheet opened above the
      // cart drawer closes itself first rather than dismissing both.
      if (stack[stack.length - 1] !== this) return;
      event.preventDefault();
      this.close();
    }

    onPageHide() {
      if (this.open) this.close();
    }
  }

  if (!customElements.get('drawer-element')) {
    customElements.define('drawer-element', Drawer);
  }

  /**
   * Delegated opener. One document-level listener serves every trigger on the
   * page, including triggers rendered after load by a section re-render, which
   * per-button listeners would miss.
   */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;

    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer || typeof drawer.toggle !== 'function') return;

    event.preventDefault();
    drawer.toggle(trigger);
  });
})();
